import React, { useState, useRef } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { validateFile, parseFreetrade } from "./csvParser";
import { invalidateUserCache } from "../../../lib/clientCache";

export function ImportDataModal({ childToParent }) {
  const [source, setSource] = useState("freetrade");
  const [fileError, setFileError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Holds all parsed transactions (all types) between preview and submit
  const allTxRef = useRef([]);

  async function handleFileChange(e) {
    setPreview(null);
    setFileError(null);
    setSubmitError(null);
    allTxRef.current = [];

    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setFileError(validation.error);
      return;
    }

    let text;
    try {
      text = await file.text();
    } catch (_) {
      setFileError("Could not read file.");
      return;
    }

    try {
      const { allTransactions, buyTransactions, summary } = parseFreetrade(text);
      if (buyTransactions.length === 0) {
        setFileError("No valid BUY transactions found in this file.");
        return;
      }
      allTxRef.current = allTransactions;
      setPreview(summary);
    } catch (err) {
      setFileError("Could not parse file: " + err.message);
    }
  }

  async function handleImport() {
    if (!allTxRef.current.length) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const fns = getFunctions();
      const importPortfolioData = httpsCallable(fns, "importPortfolioData");
      await importPortfolioData({ transactions: allTxRef.current });
      invalidateUserCache(); // holdings and transactions changed — refetch on next load
      if (childToParent) childToParent(true);
      const modalEl = document.getElementById("ImportDataModal");
      if (modalEl && window.bootstrap) {
        window.bootstrap.Modal.getInstance(modalEl)?.hide();
      }
    } catch (err) {
      setSubmitError("Import failed: " + (err.message || "Unknown error"));
    } finally {
      setIsSubmitting(false);
    }
  }

  const txWord = preview && preview.buys !== 1 ? "transactions" : "transaction";
  const tkWord = preview && preview.tickers !== 1 ? "tickers" : "ticker";
  const otherCount = preview ? preview.total - preview.buys : 0;

  return (
    <>
      <button
        type="button"
        className="stockbtn btn btn-dark justify-content-center align-items-center ms-2"
        data-bs-toggle="modal"
        data-bs-target="#ImportDataModal"
      >
        Import Data <i className="fa-solid fa-file-import"></i>
      </button>

      <div
        className="modal fade"
        id="ImportDataModal"
        tabIndex="-1"
        aria-labelledby="ImportDataModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="ImportDataModalLabel">
                Import Portfolio Data
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="importSource" className="form-label fw-semibold">
                  Import Source
                </label>
                <select
                  id="importSource"
                  className="form-select"
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                >
                  <option value="freetrade">Freetrade</option>
                </select>
              </div>

              <div className="mb-3">
                <label htmlFor="importFile" className="form-label fw-semibold">
                  Activity Export (.csv, max 5 MB)
                </label>
                <input
                  type="file"
                  id="importFile"
                  className="form-control"
                  accept=".csv"
                  onChange={handleFileChange}
                />
                {fileError && (
                  <div className="text-danger mt-1" role="alert" data-testid="file-error">
                    {fileError}
                  </div>
                )}
              </div>

              {preview && !fileError && (
                <div className="alert alert-info mb-0" role="status" data-testid="import-preview">
                  <strong>{preview.buys}</strong> BUY {txWord} across{" "}
                  <strong>{preview.tickers}</strong> {tkWord} will be added to your portfolio.
                  {otherCount > 0 && (
                    <span className="d-block mt-1 text-muted" style={{ fontSize: "0.85em" }}>
                      + {otherCount} other event{otherCount !== 1 ? "s" : ""} (dividends, interest,
                      top-ups) saved for future dividend analysis.
                    </span>
                  )}
                </div>
              )}

              {submitError && (
                <div className="alert alert-danger mb-0" role="alert" data-testid="submit-error">
                  {submitError}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleImport}
                disabled={!preview || isSubmitting}
                data-testid="import-submit-btn"
              >
                {isSubmitting ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
